import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Shield,
  ArrowRight,
  Loader,
  Check,
  Ticket,
  FileText,
  TrendingUp,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { LegalModal } from '../components/LegalModal';
import { useAuth } from '../contexts/AuthContext';
import { authApi, tokenManager } from '../services/authApi';
import { billingApi } from '../services/billingApi';
import { useForceLightMode } from '../hooks/useForceLightMode';

const CONSUMPTION_TIERS = [
  { range: '1 \u2013 1,500', rate: '30p', discount: null },
  { range: '1,501 \u2013 5,000', rate: '27p', discount: '10%' },
  { range: '5,001 \u2013 10,000', rate: '24p', discount: '20%' },
  { range: '10,001+', rate: '21p', discount: '30%' },
];

const DEFAULT_BONUS_PAGES = 1000;
const COHORT_BONUS_PAGES: Record<string, number> = {
  conference: 1500,
};
const COHORT_STORAGE_KEY = 'clinlexis_signup_cohort';

export const SignUpPage: React.FC = () => {
  useForceLightMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, logout, isAuthenticated, user, hasActiveSubscription, isLoading: authLoading, refreshUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);

  const [inviteCodeValue, setInviteCodeValue] = useState('');
  const [accessCodeOpen, setAccessCodeOpen] = useState(false);

  const cohort = (() => {
    const fromUrl = searchParams.get('cohort');
    if (fromUrl) {
      try { localStorage.setItem(COHORT_STORAGE_KEY, fromUrl.toLowerCase()); } catch { /* noop */ }
      return fromUrl.toLowerCase();
    }
    try { return localStorage.getItem(COHORT_STORAGE_KEY) || ''; } catch { return ''; }
  })();
  const bonusPages = COHORT_BONUS_PAGES[cohort] ?? DEFAULT_BONUS_PAGES;
  const formattedBonusPages = bonusPages.toLocaleString('en-GB');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    title: '',
    personaType: 'EXPERT_WITNESS' as 'EXPERT_WITNESS' | 'SOLICITOR',
  });

  const [consents, setConsents] = useState({
    termsOfService: false,
    privacyPolicy: false,
  });

  const allConsentsChecked = consents.termsOfService && consents.privacyPolicy;

  // If user is already fully set up, redirect to dashboard
  useEffect(() => {
    if (!authLoading && isAuthenticated && hasActiveSubscription) {
      navigate('/home', { replace: true });
    }
  }, [authLoading, isAuthenticated, hasActiveSubscription, navigate]);

  // Complete consents and redirect to Stripe checkout (or home for invite codes)
  const completeConsentsAndProceed = async (subscribe?: boolean) => {
    await authApi.completeProfile({
      consents: [
        { consentType: 'TERMS_OF_SERVICE', consentVersion: '1.0', granted: true },
        { consentType: 'PRIVACY_POLICY', consentVersion: '1.0', granted: true },
      ],
    });

    // Update stored user
    const storedUser = tokenManager.getStoredUser();
    if (storedUser) {
      tokenManager.storeTokens({
        accessToken: tokenManager.getAccessToken() || '',
        refreshToken: tokenManager.getRefreshToken() || '',
        tokenType: 'Bearer',
        expiresIn: 0,
        user: { ...storedUser, consentsCompleted: true }
      });
    }

    if (subscribe) {
      const email = user?.email || formData.email;
      const checkoutSession = await billingApi.createCheckoutSession(email, cohort || undefined);
      billingApi.redirectToCheckout(checkoutSession.checkoutUrl);
    } else {
      await refreshUser();
      navigate('/home', { replace: true });
    }
  };

  // Handle "Continue" for returning users who already have an account
  const handleContinue = async () => {
    setIsLoading(true);
    setError('');
    try {
      if (!user?.consentsCompleted) {
        if (!allConsentsChecked) {
          setError('Please accept the Terms of Service and Privacy Policy to continue.');
          setIsLoading(false);
          return;
        }
        await completeConsentsAndProceed(true);
      } else {
        // Consents done — go straight to Stripe checkout
        const checkoutSession = await billingApi.createCheckoutSession(user?.email || '', cohort || undefined);
        billingApi.redirectToCheckout(checkoutSession.checkoutUrl);
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to proceed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Create user account (this also logs them in and sets JWT token)
      const signUpData = inviteCodeValue.trim()
        ? { ...formData, inviteCode: inviteCodeValue.trim() }
        : formData;
      await signUp(signUpData);

      // Complete consents immediately, then proceed
      const subscribe = !inviteCodeValue.trim();
      await completeConsentsAndProceed(subscribe);

    } catch (err: any) {
      console.error('Registration error:', err);

      // Extract field errors if available
      let errorMessage = err instanceof Error ? err.message : 'Registration failed. Please try again.';

      if (err.details && err.details.fieldErrors) {
        const fieldErrors = err.details.fieldErrors;
        const errorMessages = Object.entries(fieldErrors)
          .map(([field, message]) => `${message}`)
          .join('. ');
        errorMessage = errorMessages || errorMessage;
      }

      // Add helpful guidance for invite code errors
      if (err.code === 'INVALID_INVITE_CODE') {
        errorMessage += ' Please check your code and try again, or contact us at support@clinlexis.co.uk for help.';
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const consentCheckboxes = (
    <div className="space-y-3">
      <label className="flex items-start space-x-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={consents.termsOfService}
          onChange={(e) => setConsents({ ...consents, termsOfService: e.target.checked })}
          className="mt-1 h-4 w-4 rounded border-medical-300 text-primary-600 focus:ring-primary-500"
        />
        <div className="flex items-center space-x-2">
          <FileText className="h-4 w-4 text-primary-500" />
          <span className="text-sm text-medical-700 group-hover:text-medical-900">
            I agree to the{' '}
            <span
              className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegalModal('terms'); }}
            >
              Terms of Service
            </span>
            {' '}(v1.0) <span className="text-red-500">*</span>
          </span>
        </div>
      </label>

      <label className="flex items-start space-x-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={consents.privacyPolicy}
          onChange={(e) => setConsents({ ...consents, privacyPolicy: e.target.checked })}
          className="mt-1 h-4 w-4 rounded border-medical-300 text-primary-600 focus:ring-primary-500"
        />
        <div className="flex items-center space-x-2">
          <Lock className="h-4 w-4 text-primary-500" />
          <span className="text-sm text-medical-700 group-hover:text-medical-900">
            I agree to the{' '}
            <span
              className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegalModal('privacy'); }}
            >
              Privacy Policy
            </span>
            {' '}(v1.0) <span className="text-red-500">*</span>
          </span>
        </div>
      </label>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50">
      {/* Legal Modal */}
      <LegalModal
        isOpen={legalModal !== null}
        onClose={() => setLegalModal(null)}
        type={legalModal || 'terms'}
      />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-medical-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo size="md" />
            <div className="flex items-center space-x-4">
              {!isAuthenticated ? (
                <Link
                  to="/signin"
                  className="text-medical-600 hover:text-medical-900 font-medium"
                >
                  Sign In
                </Link>
              ) : (
                <button
                  onClick={() => logout()}
                  className="text-medical-600 hover:text-medical-900 font-medium"
                >
                  Sign Out
                </button>
              )}
              <Link
                to="/"
                className="btn-secondary"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-8">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-medical-900 mb-3">
            Get Started with ClinLexis
          </h1>
          <p className="text-medical-600 max-w-2xl mx-auto">
            One simple plan. Full platform access. Pay only for the pages you process.
          </p>
          {cohort === 'conference' && (
            <div className="inline-block mt-4 px-4 py-2 rounded-full bg-amber-50 border border-amber-200">
              <span className="text-sm font-semibold text-amber-800">
                Conference offer applied &mdash; {formattedBonusPages} free pages on sign-up
              </span>
            </div>
          )}
        </div>

        {/* Plan Card */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="bg-white rounded-2xl shadow-lg border-2 border-primary-200 p-8">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-medical-900">ClinLexis</h2>
                <p className="text-sm text-medical-500">Monthly subscription</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-medical-900">
                  {'\u00A3'}24.99<span className="text-base font-normal text-medical-500">/mo</span>
                </p>
                <p className="text-xs text-medical-500">+ VAT</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{formattedBonusPages}</p>
                <p className="text-xs text-emerald-600 font-medium">free pages on sign-up</p>
              </div>
              <div className="bg-primary-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-primary-700">Full Access</p>
                <p className="text-xs text-primary-600 font-medium">all features included</p>
              </div>
              <div className="bg-cyan-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-cyan-700">VAT Invoices</p>
                <p className="text-xs text-cyan-600 font-medium">per case on completion</p>
              </div>
            </div>

            <ul className="text-sm text-medical-700 space-y-2 mb-6">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                AI-powered chronology, metadata extraction, and case analysis
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                Unlimited reports, exports, and assistant queries after processing
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                UK data residency &mdash; documents never leave the UK
              </li>
            </ul>

            {/* Consumption tier breakdown */}
            <div className="bg-medical-50 rounded-lg border border-medical-200 p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp className="h-4 w-4 text-medical-500" />
                <p className="text-xs font-semibold text-medical-600 uppercase tracking-wide">Pay-as-you-go page pricing</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-medical-500">
                    <th className="text-left py-1 font-medium">Pages / month</th>
                    <th className="text-right py-1 font-medium">Rate</th>
                    <th className="text-right py-1 font-medium">Saving</th>
                  </tr>
                </thead>
                <tbody className="text-medical-700">
                  {CONSUMPTION_TIERS.map((tier, i) => (
                    <tr key={i} className="border-t border-medical-200">
                      <td className="py-1.5">{tier.range}</td>
                      <td className="py-1.5 text-right font-medium">{tier.rate}</td>
                      <td className="py-1.5 text-right">{tier.discount ? <span className="text-emerald-600 font-medium">{tier.discount}</span> : '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-medical-400 mt-2">Tiers reset on the 1st of each month. Free pages are consumed first.</p>
            </div>
          </div>
        </div>

        {/* Sign Up Form / Continue to Payment */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-medical-200 p-8">
            {isAuthenticated ? (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-medical-900 mb-2">
                    Welcome Back{user?.firstName ? `, ${user.firstName}` : ''}
                  </h2>
                  <p className="text-medical-600">
                    Subscribe to ClinLexis to get started.
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {!user?.consentsCompleted && (
                  <div className="mb-5">
                    {consentCheckboxes}
                  </div>
                )}

                <button
                  onClick={handleContinue}
                  disabled={isLoading || (!user?.consentsCompleted && !allConsentsChecked)}
                  className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Redirecting to Payment...</span>
                    </>
                  ) : (
                    <>
                      <span>Continue to Payment</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-medical-900 mb-2">
                    Create Your Account
                  </h2>
                  <p className="text-medical-600">
                    {inviteCodeValue.trim() ? (
                      <span className="font-semibold text-purple-600">Using access code</span>
                    ) : (
                      <span>You'll be directed to payment after registration</span>
                    )}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-medical-700 mb-2">
                      I am signing up as
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, personaType: 'EXPERT_WITNESS'})}
                        className={`text-left p-3 rounded-lg border-2 transition-colors ${
                          formData.personaType === 'EXPERT_WITNESS'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-medical-200 hover:border-medical-300'
                        }`}
                      >
                        <div className="font-medium text-medical-900">Expert Witness</div>
                        <div className="text-xs text-medical-600 mt-0.5">
                          Clinician producing reports for the courts
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, personaType: 'SOLICITOR'})}
                        className={`text-left p-3 rounded-lg border-2 transition-colors ${
                          formData.personaType === 'SOLICITOR'
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-medical-200 hover:border-medical-300'
                        }`}
                      >
                        <div className="font-medium text-medical-900">Solicitor</div>
                        <div className="text-xs text-medical-600 mt-0.5">
                          Drafting case briefs, letters of instruction, and letters of claim
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-[1fr_2fr_2fr] gap-4">
                    <div>
                      <label className="block text-sm font-medium text-medical-700 mb-2">
                        Title
                      </label>
                      <select
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="input-field appearance-none"
                      >
                        <option value="">Select</option>
                        <option value="Dr">Dr</option>
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Miss">Miss</option>
                        <option value="Ms">Ms</option>
                        <option value="Prof">Prof</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-medical-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        className="input-field"
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-medical-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        className="input-field"
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-medical-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400" />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="input-field pl-10"
                        placeholder="your.email@example.co.uk"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-medical-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className="input-field pl-10 pr-10"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-medical-400 hover:text-medical-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-medical-200 pt-4">
                    {consentCheckboxes}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !allConsentsChecked}
                    className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Creating Account...</span>
                      </>
                    ) : (
                      <>
                        <span>Create Account & Continue</span>
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-medical-600">
                    Already have an account?{' '}
                    <Link
                      to="/signin"
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Sign In
                    </Link>
                  </p>
                </div>

                <div className="mt-4 pt-4 border-t border-medical-100 text-center">
                  {!accessCodeOpen ? (
                    <button
                      type="button"
                      onClick={() => setAccessCodeOpen(true)}
                      className="text-xs text-medical-500 hover:text-medical-700 inline-flex items-center gap-1.5"
                    >
                      <Ticket className="h-3.5 w-3.5" />
                      Have an access code?
                    </button>
                  ) : (
                    <div className="text-left">
                      <label className="block text-xs font-medium text-medical-600 mb-1.5">
                        Access code
                      </label>
                      <input
                        type="text"
                        value={inviteCodeValue}
                        onChange={(e) => setInviteCodeValue(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        maxLength={20}
                        className="input-field uppercase tracking-widest font-mono text-center text-sm py-2"
                      />
                      <p className="text-[11px] text-medical-400 mt-1.5">
                        Skips checkout and grants complimentary pages. For approved partners only.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}


            {/* Security Badge */}
            <div className="mt-6 flex items-center justify-center space-x-2 text-xs text-medical-500">
              <Shield className="h-4 w-4" />
              <span>256-bit SSL encryption &bull; GDPR compliant &bull; DSPT compliant</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
